import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import UserAvatar from '../../../features/profile/components/shared/user-avatar';
import { User } from '../../../features/profile/utils';
import { useAutoLogout } from '../../../hooks/useAuth0AutoLogout';

interface AccountMenuProps {
    user?: User;
    avatar?: string;
    isPortalUser: boolean;
}

const AccountMenu: React.FC<AccountMenuProps> = ({ user, isPortalUser, avatar }) => {
    const { t } = useTranslation();
    const { userLogout } = useAutoLogout();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onLogout = () => {
        userLogout();
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textAlign: 'center',
                    ml: 'auto',
                    cursor: 'pointer',
                }}
                onClick={handleClick}
            >
                <Typography>{t('my-account')}</Typography>
                <IconButton
                    data-testid='my-account-select'
                    color='secondary'
                    size='small'
                    sx={{ ml: '4px' }}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup='true'
                    aria-expanded={open ? 'true' : undefined}
                >
                    <UserAvatar firstName={user?.firstName} lastName={user?.lastName} avatarLink={avatar} />
                </IconButton>
            </Box>

            <Menu
                autoFocus={false}
                anchorEl={anchorEl}
                id='account-menu'
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                sx={{ marginTop: '14px' }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Typography
                    component={MenuItem}
                    color='common.grey1'
                    sx={{
                        fontSize: '14px',
                        minWidth: '196px',
                        padding: '12px 16px',
                    }}
                >
                    {user?.email}
                </Typography>
                <Divider />
                {isPortalUser && (
                    <MenuItem component={Link} color='common.black' href='/preferences' underline='none'>
                        {t('preferences')}
                    </MenuItem>
                )}
                <MenuItem component={Link} color='common.black' href='/profile' underline='none'>
                    {t('my-profile')}
                </MenuItem>
                <MenuItem onClick={onLogout}>{t('logout')}</MenuItem>
            </Menu>
        </>
    );
};

export default AccountMenu;
